import { useState } from 'react'
import { BreakdownExceedsTotalError, breakdownRemainder } from '@domain/finance'
import type { BreakdownItem, Category, Transaction } from '@domain/finance'
import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { parseAmountInput } from '@shared/money/parse'
import { SeletorDeCategoria } from './SeletorDeCategoria'
import { Botao } from '@ui/glass/Botao'
import { IconeCheque, IconeDividir, IconeMais, IconeX } from '@ui/shell/icones'
import { splitEqually } from './breakdown-split'
import { useBreakdown } from './use-breakdown'

/**
 * O detalhamento — *"o coração da tela"*, em
 * `Docs/04-Design/Telas/Detalhe da transação.md`.
 *
 * ## A linha "Não detalhado" nunca some
 *
 * A nota é literal: *"Ela nunca some, nunca é escondida, e carrega a categoria
 * da própria transação. É assim que o invariante fica visível em vez de virar
 * surpresa numa soma de relatório"*. Ela só desaparece quando vale zero — e aí
 * não há resto a mostrar.
 *
 * ## O resumo é `breakdownRemainder`, chamada, não refeita
 *
 * A mesma função que a Function do servidor chama. O plano da fase diz por quê:
 * *"recalcular à mão daria três lugares para errar o mesmo invariante e nenhum
 * para perceber que discordam"*.
 *
 * ## A validação acontece ANTES de gravar
 *
 * Somar mais que o total é impedido aqui **e** na Function — é a definição de
 * pronto da fase. Aqui a resposta é "cabe no máximo R$ x"; lá é um log e um
 * resumo que não avança. As duas usam a mesma função e por isso não têm como
 * discordar sobre o que é "passar".
 */

function nomeDe(categoryId: string, categories: Category[]): string {
  return categories.find((c) => c.id === categoryId)?.name ?? 'categoria que não existe mais'
}

/** Uma linha de item, com edição de valor e remoção. */
function Linha({
  item,
  categories,
  maxCents,
  onEdit,
  onRemove,
}: {
  item: BreakdownItem
  categories: Category[]
  /** O teto deste item: o que ele já vale mais o resto disponível. */
  maxCents: number
  onEdit: (patch: { categoryId?: string; amountCents?: number }) => Promise<void>
  onRemove: () => Promise<void>
}) {
  const [erro, setErro] = useState<string | null>(null)

  return (
    <li className="flex flex-wrap items-baseline gap-2 border-b border-[var(--border)] py-1">
      <span className="flex-1">{nomeDe(item.categoryId, categories)}</span>
      <label>
        <span className="sr-only">{`Valor de ${nomeDe(item.categoryId, categories)}`}</span>
        <input
          inputMode="decimal"
          defaultValue={(item.amountCents / 100).toFixed(2).replace('.', ',')}
          className="w-24 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-right tabular-nums"
          onBlur={(event) => {
            const parsed = parseAmountInput(event.target.value)
            if (parsed.status !== 'ok') {
              setErro('valor inválido')
              return
            }
            // Em módulo: o sinal mora na transação, e "-50" digitado aqui quer
            // dizer cinquenta reais desta parte.
            const amountCents = Math.abs(parsed.cents)
            if (amountCents === item.amountCents) return
            if (amountCents > maxCents) {
              setErro(`cabe no máximo ${formatBRL(maxCents)}`)
              return
            }
            setErro(null)
            void onEdit({ amountCents })
          }}
        />
      </label>
      <button
        type="button"
        className="alvo justify-start text-xs underline"
        onClick={() => {
          void onRemove()
        }}
      >
        {`Remover ${nomeDe(item.categoryId, categories)}`}
      </button>
      {erro !== null && (
        <span role="alert" className="w-full text-xs text-[var(--text-muted)]">
          {erro}
        </span>
      )}
    </li>
  )
}

export function BreakdownSection({
  transaction,
  categories,
  onCategoriasMudaram,
}: {
  transaction: Transaction
  categories: Category[]
  /** Uma categoria criada do compositor — a ficha lá de cima que releia. */
  onCategoriasMudaram: () => void
}) {
  const breakdown = useBreakdown(transaction.id)
  const [categoria, setCategoria] = useState<string | null>(null)
  const [valor, setValor] = useState('')
  const [partes, setPartes] = useState('2')
  const [erro, setErro] = useState<string | null>(null)
  /** Qual dos dois compositores está aberto. `null` é o estado de repouso. */
  const [aberto, setAberto] = useState<'item' | 'partes' | null>(null)

  function fechar(): void {
    setAberto(null)
    setErro(null)
  }

  // **Um `!== 'loaded'` só, e não dois `===`.** O primeiro membro da união tem
  // `status: 'loading' | 'failed'`, e o TypeScript não estreita para o segundo
  // membro dentro dos closures deste componente quando a eliminação vem de dois
  // testes em sequência. Com um guarda só, `breakdown` é o membro carregado do
  // ponto seguinte em diante — inclusive dentro de `adicionar` e `dividir`.
  if (breakdown.status !== 'loaded') {
    return breakdown.status === 'loading' ? (
      <p role="status" className="mt-4 text-sm text-[var(--text-muted)]">
        Carregando o detalhamento…
      </p>
    ) : (
      <p role="alert" className="mt-4 text-sm text-[var(--text-muted)]">
        Não foi possível carregar o detalhamento agora.
      </p>
    )
  }

  const { items } = breakdown
  // A mesma função que a Function chama. Se ela lançar aqui, os dados já estão
  // inconsistentes — e a tela é o lugar onde alguém pode consertar.
  let resumo
  try {
    resumo = breakdownRemainder(transaction.source.amountCents, items)
  } catch (error) {
    return (
      <p role="alert" className="mt-4 text-sm text-[var(--text-muted)]">
        {error instanceof BreakdownExceedsTotalError
          ? 'Os itens somam mais que a transação. Remova ou reduza algum para o detalhamento fechar.'
          : 'O detalhamento desta transação tem um valor inválido.'}
      </p>
    )
  }

  const adicionar = () => {
    if (categoria === null) {
      setErro('escolha a categoria')
      return
    }
    const parsed = parseAmountInput(valor)
    if (parsed.status !== 'ok') {
      setErro('valor inválido')
      return
    }
    const amountCents = Math.abs(parsed.cents)
    if (amountCents === 0) {
      setErro('o valor precisa ser maior que zero')
      return
    }
    if (amountCents > resumo.remainderCents) {
      setErro(`cabe no máximo ${formatBRL(resumo.remainderCents)}`)
      return
    }
    setErro(null)
    void breakdown.add({ categoryId: categoria, amountCents, description: null }).then(() => {
      setValor('')
    })
  }

  const dividir = () => {
    if (categoria === null) {
      setErro('escolha a categoria das partes')
      return
    }
    const n = Number(partes)
    const fatias = splitEqually(resumo.remainderCents, n)
    if (fatias.length === 0) {
      setErro('número de partes inválido')
      return
    }
    setErro(null)
    // Todas na mesma categoria: o atalho reparte o valor, e trocar a categoria
    // de cada fatia é edição — dividir e categorizar de uma vez exigiria um
    // formulário por parte antes de existir uma parte.
    void breakdown.addMany(fatias.map((amountCents) => ({ categoryId: categoria, amountCents })))
  }

  return (
    <section aria-label="Detalhamento" className="mt-4 border-t border-[var(--border)] pt-3">
      <h2 className="mb-2 text-xs text-[var(--text-muted)]">
        {`Detalhamento (${String(resumo.itemCount)} ${resumo.itemCount === 1 ? 'item' : 'itens'})`}
      </h2>

      <ul className="text-sm">
        {items.map((item) => (
          <Linha
            key={item.id}
            item={item}
            categories={categories}
            maxCents={item.amountCents + resumo.remainderCents}
            onEdit={(patch) => breakdown.edit(item.id, patch)}
            onRemove={() => breakdown.remove(item.id)}
          />
        ))}
        {resumo.remainderCents > 0 && (
          <li className="flex items-baseline gap-2 py-1 text-[var(--text-muted)]">
            {/* Nunca some, nunca é escondida. Carrega a categoria da própria
                transação — é onde o resto vive. */}
            <span className="flex-1">
              {`Não detalhado${
                transaction.enrichment.categoryId === null
                  ? ''
                  : ` · ${nomeDe(transaction.enrichment.categoryId, categories)}`
              }`}
            </span>
            <span className="tabular-nums">{formatBRL(absCents(resumo.remainderCents))}</span>
          </li>
        )}
      </ul>

      {/* **Fechado por padrão.** O compositor vivia aberto no fim da seção:
          cinco controles de pesos diferentes numa `flex-wrap` que, em 390px,
          quebrava em três linhas tortas — o seletor numa, "Adicionar item"
          debaixo do campo de valor, e "ou dividir o resto em" pendurado no
          meio. E ele ocupava esse espaço mesmo nas transações que ninguém vai
          detalhar, que são a maioria.

          Agora são dois botões, e cada um abre a sua linha — o mesmo movimento
          da decisão 246a, quando o formulário sempre aberto das categorias
          virou folha. */}
      {aberto === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Botao
            type="button"
            className="text-xs"
            onClick={() => {
              setAberto('item')
            }}
          >
            <IconeMais />
            Adicionar item
          </Botao>
          {/* Só quando há resto: dividir zero em três partes é uma conta sem
              resposta, e o botão que a oferece só decepciona. */}
          {resumo.remainderCents > 0 && (
            <Botao
              type="button"
              className="text-xs"
              onClick={() => {
                setAberto('partes')
              }}
            >
              <IconeDividir />
              Dividir o resto
            </Botao>
          )}
        </div>
      )}

      {aberto === 'item' && (
        // Uma coisa por linha, e o valor alinhado à direita como a coluna de
        // cima — era isso que não batia: o campo de valor era o único número da
        // seção fora do alinhamento dos outros.
        <div className="mt-3 flex flex-col gap-2">
          <SeletorDeCategoria
            categorias={categories}
            value={categoria}
            rotulo="Categoria do item"
            onChange={setCategoria}
            onCriou={onCategoriasMudaram}
          />
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <span className="sr-only">Valor do item</span>
              <input
                inputMode="decimal"
                placeholder="valor"
                value={valor}
                className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-right text-base tabular-nums"
                onChange={(event) => {
                  setValor(event.target.value)
                }}
              />
            </label>
            {/* Ícone puro nos DOIS, e com nome no rótulo: confirmar e cancelar
                são o par mais convencional que existe num formulário, e escrever
                "Adicionar item" ao lado de um campo chamado "valor" gastaria a
                largura que o campo precisa. */}
            <Botao type="button" tom="primario" aria-label="Adicionar item" onClick={adicionar}>
              <IconeCheque />
            </Botao>
            <Botao type="button" aria-label="Cancelar" onClick={fechar}>
              <IconeX />
            </Botao>
          </div>
        </div>
      )}

      {aberto === 'partes' && (
        // O seletor de categoria vem JUNTO, e não é o mesmo campo lá de cima:
        // `dividir` exige uma categoria — as fatias precisam ir para algum
        // lugar. Na versão anterior o seletor era compartilhado pelos dois
        // botões, e essa dependência ficava invisível: quem tocasse em "Partes
        // iguais" sem ter mexido no seletor recebia "escolha a categoria das
        // partes" vindo de um campo que parecia ser do outro botão.
        <div className="mt-3 flex flex-col gap-2">
          <SeletorDeCategoria
            categorias={categories}
            value={categoria}
            rotulo="Categoria das partes"
            onChange={setCategoria}
            onCriou={onCategoriasMudaram}
          />
          <div className="flex items-center gap-2">
          <label className="flex flex-1 items-center gap-2 text-sm text-[var(--text-muted)]">
            <span>Dividir em</span>
            {/* `aria-label` no campo, e não um `<span class="sr-only">` dentro
                do `<label>`: com texto visível dos dois lados, o nome acessível
                viraria "Dividir em Número de partes partes". */}
            <input
              inputMode="numeric"
              aria-label="Número de partes"
              value={partes}
              className="w-16 rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-right text-base tabular-nums"
              onChange={(event) => {
                setPartes(event.target.value)
              }}
            />
            <span>partes</span>
          </label>
            <Botao type="button" tom="primario" aria-label="Partes iguais" onClick={dividir}>
              <IconeCheque />
            </Botao>
            <Botao type="button" aria-label="Cancelar" onClick={fechar}>
              <IconeX />
            </Botao>
          </div>
        </div>
      )}


      {erro !== null && (
        <p role="alert" className="mt-2 text-sm text-[var(--text-muted)]">
          {erro}
        </p>
      )}
    </section>
  )
}
